import { makeStyles } from '@material-ui/core';

export const COLORS = {
  brand: '#D9042F',
  brandHover: '#B80328',
  brandSoft: '#FDEBEE',
  brandRing: 'rgba(217, 4, 47, 0.2)',
  text: '#111827',
  textBody: '#374151',
  textMuted: '#6b7280',
  textSubtle: '#9ca3af',
  border: '#e5e7eb',
  borderInput: '#d1d5db',
  borderSoft: '#f3f4f6',
  surfaceMuted: '#f8fafc',
  surfaceFooter: '#f9fafb',
  surfaceTrack: '#e5e7eb'
};

const useStyles = makeStyles(theme => ({
  // ---- Modal shell ----
  dialogPaper: {
    borderRadius: 16,
    maxHeight: '92vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow:
      '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'
  },
  dialogBackdrop: {
    backgroundColor: 'rgba(15, 23, 42, 0.6)'
  },
  dialogContent: {
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  dialogContentModal: {
    flex: 1,
    overflow: 'hidden'
  },
  modalHeader: {
    borderBottom: `1px solid ${COLORS.borderSoft}`,
    flexShrink: 0
  },
  modalHeaderPadded: {
    padding: theme.spacing(2.5, 3.5, 0),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2, 2, 0)
    }
  },
  modalTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  modalTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.text,
    letterSpacing: '-0.01em',
    lineHeight: 1.4
  },
  idChip: {
    padding: '2px 8px',
    borderRadius: 4,
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3
  },
  closeButton: {
    width: 32,
    height: 32,
    color: COLORS.textSubtle,
    '&:hover': {
      color: COLORS.textBody,
      backgroundColor: '#f3f4f6'
    }
  },
  tabs: {
    minHeight: 0,
    marginTop: theme.spacing(1),
    '& .MuiTabs-flexContainer': {
      gap: theme.spacing(4),
      [theme.breakpoints.down('xs')]: {
        gap: theme.spacing(2)
      }
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: 14,
      fontWeight: 500,
      minHeight: 0,
      minWidth: 0,
      padding: theme.spacing(0, 0, 1.5),
      color: COLORS.textMuted,
      opacity: 1,
      '&:hover': {
        color: '#1f2937'
      }
    },
    '& .Mui-selected': {
      color: `${COLORS.brand} !important`,
      fontWeight: 600
    },
    '& .MuiTabs-indicator': {
      backgroundColor: COLORS.brand,
      height: 2
    }
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0
  },
  bodyContent: {
    backgroundColor: '#ffffff',
    padding: theme.spacing(1, 0, 2)
  },
  bodyContentModal: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1, 3.5, 2.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 2, 2)
    },
    '&::-webkit-scrollbar': {
      width: 6
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f5f9'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#cbd5e1',
      borderRadius: 999
    }
  },

  // ---- Sections ----
  sectionCard: {
    paddingTop: theme.spacing(2),
    '& + &': {
      paddingTop: theme.spacing(3)
    }
  },
  sectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  sectionTitleMain: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  sectionTitleAccent: {
    width: 6,
    height: 16,
    borderRadius: 999,
    backgroundColor: COLORS.brand,
    flexShrink: 0
  },
  sectionTitleText: {
    fontSize: 13,
    fontWeight: 700,
    color: COLORS.text,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    lineHeight: 1.3
  },
  sectionTitleExtra: {
    fontSize: 13,
    fontWeight: 500,
    color: COLORS.textMuted
  },

  // ---- Fields ----
  fieldLabel: {
    display: 'block',
    fontSize: 13,
    fontWeight: 400,
    color: COLORS.textBody,
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.75)
  },
  fieldLabelNoWrap: {
    display: 'flex',
    alignItems: 'baseline',
    whiteSpace: 'nowrap'
  },
  fieldLabelText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  fieldHint: {
    marginLeft: 4,
    color: COLORS.textSubtle,
    fontWeight: 400
  },
  requiredMark: {
    color: COLORS.brand,
    marginLeft: 4
  },
  fieldIcon: {
    fontSize: 14,
    color: COLORS.textSubtle
  },
  unitText: {
    fontSize: 13,
    fontWeight: 500,
    color: COLORS.textSubtle
  },
  textField: {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      borderRadius: 8,
      minHeight: 40,
      transition: 'box-shadow 0.15s',
      '& fieldset': {
        borderColor: COLORS.borderInput
      },
      '&:hover fieldset': {
        borderColor: '#9ca3af'
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${COLORS.brandRing}`
      },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.brand,
        borderWidth: 1
      },
      '&.Mui-disabled': {
        backgroundColor: COLORS.surfaceMuted,
        '& fieldset': {
          borderColor: '#e2e8f0'
        }
      },
      '&.Mui-error fieldset': {
        borderColor: '#f44336'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 12px',
      fontSize: 13,
      fontWeight: 600,
      color: COLORS.text,
      '&.Mui-disabled': {
        color: '#475569'
      }
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      paddingTop: 0,
      paddingBottom: 0,
      '& .MuiAutocomplete-input': {
        padding: '10px 4px'
      }
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      marginRight: 0
    }
  },
  monoInput: {
    '& .MuiOutlinedInput-input': {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      letterSpacing: '0.04em'
    }
  },
  plateInput: {
    '& .MuiOutlinedInput-input': {
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.06em'
    }
  },
  strongInput: {
    '& .MuiOutlinedInput-input': {
      fontWeight: 600
    }
  },
  switchFieldHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minHeight: 24,
    marginBottom: theme.spacing(0.75),
    '& $fieldLabel': {
      marginBottom: 0
    }
  },

  // ---- Feature cards (Cài đặt hệ thống) ----
  featureCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75),
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    transition: 'border-color 0.15s',
    '&:hover': {
      borderColor: COLORS.borderInput
    }
  },
  featureCardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minHeight: 20
  },
  featureCardTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.text,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  featureBadge: {
    flexShrink: 0,
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3
  },
  featureBadgeSuccess: {
    backgroundColor: '#d1fae5',
    color: '#047857'
  },
  featureBadgeWarning: {
    backgroundColor: '#ffedd5',
    color: '#c2410c'
  },
  featureCardFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.25),
    borderTop: `1px solid ${COLORS.borderSoft}`
  },
  featureCardStatus: {
    fontSize: 11,
    fontWeight: 500,
    color: COLORS.textSubtle
  },
  segmented: {
    display: 'inline-flex',
    padding: 4,
    gap: 2,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
    border: `1px solid ${COLORS.border}`
  },
  segmentedItem: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px 12px',
    borderRadius: 6,
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    color: '#4b5563',
    transition: 'background-color 0.15s, color 0.15s',
    '&:hover:not(:disabled)': {
      color: COLORS.text,
      backgroundColor: 'rgba(255, 255, 255, 0.6)'
    },
    '&:focus-visible': {
      outline: `2px solid ${COLORS.brandRing}`
    },
    '&:disabled': {
      cursor: 'not-allowed',
      color: '#c4c8ce'
    }
  },
  segmentedItemActive: {
    backgroundColor: `${COLORS.brand} !important`,
    color: '#ffffff !important',
    fontWeight: 700,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)'
  },

  // ---- Cấu hình camera ----
  cameraCardTitle: {
    whiteSpace: 'normal'
  },
  cameraInputRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.25),
    borderTop: `1px solid ${COLORS.borderSoft}`
  },
  // ---- Setting rows (tab tích truyền) ----
  settingItem: {
    minHeight: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    borderRadius: 12,
    padding: theme.spacing(1.5, 1.75),
    backgroundColor: '#ffffff',
    border: `1px solid ${COLORS.border}`
  },
  settingItemFill: {
    height: '100%',
    boxSizing: 'border-box'
  },
  settingLabel: {
    marginBottom: '0 !important',
    color: COLORS.text,
    fontWeight: 600
  },

  // ---- Footer ----
  footerBar: {
    flexShrink: 0,
    borderTop: `1px solid ${COLORS.border}`,
    padding: theme.spacing(1.5, 0)
  },
  footerBarModal: {
    backgroundColor: COLORS.surfaceFooter,
    padding: theme.spacing(2, 3.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 2)
    }
  },
  groupButtonSubmit: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'stretch'
    }
  },
  commitmentBox: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#ffffff',
    border: '1px solid #fecaca',
    borderRadius: 10,
    padding: theme.spacing(1, 1.5),
    '& .MuiFormControlLabel-root': {
      alignItems: 'center',
      margin: 0
    },
    '& .MuiCheckbox-root': {
      padding: 9,
      marginRight: theme.spacing(1)
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 13,
      lineHeight: 1.4,
      color: COLORS.text
    }
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  buttonProgress: {
    color: COLORS.brand,
    position: 'absolute',
    top: '50%',
    right: 12,
    marginTop: -12,
    marginLeft: 0
  },
  buttonIcon: {
    fontSize: '14px !important'
  },
  primaryButton: {
    minWidth: 100,
    borderRadius: 8,
    backgroundColor: COLORS.brand,
    color: '#ffffff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(1, 3),
    '& .MuiButton-startIcon': {
      marginRight: 6
    },
    '&:hover': {
      backgroundColor: COLORS.brandHover,
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)'
    },
    '&.Mui-disabled': {
      backgroundColor: '#F09AAB',
      color: '#ffffff'
    },
    [theme.breakpoints.down('sm')]: {
      flex: 1
    }
  },
  secondaryButton: {
    minWidth: 88,
    borderRadius: 8,
    borderColor: COLORS.borderInput,
    color: COLORS.textBody,
    backgroundColor: '#ffffff',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(0.875, 2.5),
    '&:hover': {
      borderColor: COLORS.borderInput,
      backgroundColor: '#f3f4f6',
      color: COLORS.text
    },
    [theme.breakpoints.down('sm')]: {
      flex: 1
    }
  },

  // ---- Fuel sensor tab ----
  sensorHint: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: COLORS.textMuted,
    fontSize: 12,
    fontStyle: 'italic',
    minHeight: 40,
    marginTop: theme.spacing(1)
  },
  fuelSensorPanel: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    backgroundColor: '#ffffff'
  },
  fuelSensorHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 13
    }
  },
  sensorValueBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  sensorValueText: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.text
  },
  refreshButton: {
    color: COLORS.brand
  },
  fuelCalibrationRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  fuelCalibrationInput: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff'
    }
  },
  removeCalibrationButton: {
    color: COLORS.textSubtle,
    padding: 6,
    marginTop: 4,
    '&:hover': {
      color: COLORS.brand
    },
    [theme.breakpoints.down('sm')]: {
      justifySelf: 'flex-end'
    }
  },
  addCalibrationButton: {
    borderStyle: 'dashed',
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textMuted,
    borderColor: COLORS.borderInput,
    borderRadius: 8,
    padding: theme.spacing(1.25),
    '&:hover': {
      borderStyle: 'dashed',
      borderColor: COLORS.brand,
      color: COLORS.brand,
      backgroundColor: COLORS.brandSoft
    }
  },
  inlineErrorText: {
    display: 'block',
    marginTop: 8,
    color: '#f44336',
    fontSize: 12
  },
  emptyTabText: {
    padding: theme.spacing(3, 0),
    textAlign: 'center',
    color: COLORS.textMuted,
    fontSize: 13
  },

  // ---- Switch ----
  customSwitchContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    flexShrink: 0
  },
  customSwitchRoot: {
    width: 32,
    height: 18,
    padding: 0
  },
  customSwitchSmall: {
    width: 28,
    height: 16,
    '& $customSwitchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(12px)'
      }
    },
    '& $customSwitchThumb': {
      width: 12,
      height: 12
    }
  },
  customSwitchBase: {
    padding: 2,
    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&.Mui-checked': {
      transform: 'translateX(14px)',
      '& + $customSwitchTrack': {
        backgroundColor: COLORS.brand,
        opacity: 1
      }
    },
    '&.Mui-disabled + $customSwitchTrack': {
      opacity: 0.5
    }
  },
  customSwitchThumb: {
    width: 14,
    height: 14,
    borderRadius: 999,
    backgroundColor: '#fff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)'
  },
  customSwitchTrack: {
    borderRadius: 999,
    backgroundColor: COLORS.surfaceTrack,
    opacity: 1,
    transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  }
}));

export default useStyles;
